/**
 * useElapsedTime Hook
 *
 * Custom hook for calculating and displaying elapsed time from a start timestamp.
 * Updates every second while active, handles background tabs correctly by
 * calculating from startTime rather than incrementing.
 */

import { useState, useEffect, useCallback } from 'react';

interface UseElapsedTimeOptions {
  /** Update interval in milliseconds (default: 1000ms) */
  interval?: number;
  /** Whether the timer is active (default: true) */
  isActive?: boolean;
  /** Total paused time in milliseconds */
  totalPausedTime?: number;
  /** Whether the timer is currently paused */
  isPaused?: boolean;
  /** Timestamp when timer was paused */
  pausedAt?: number;
}

interface UseElapsedTimeReturn {
  /** Elapsed time in seconds */
  elapsedSeconds: number;
  /** Elapsed time in minutes (rounded down) */
  elapsedMinutes: number;
  /** Formatted time string (MM:SS or HH:MM:SS) */
  formatted: string;
  /** Formatted time string with hours always shown */
  formattedWithHours: string;
}

/**
 * Hook that tracks elapsed time from a start timestamp
 *
 * @param startTime - Unix timestamp in milliseconds when the timer started, or null if not active
 * @param options - Configuration options
 * @returns Object containing elapsed time in various formats
 *
 * @example
 * ```tsx
 * const { formatted, elapsedMinutes } = useElapsedTime(timer.startTime);
 * return <span>{formatted}</span>; // "5:23" or "1:05:23"
 * ```
 */
export function useElapsedTime(
  startTime: number | null,
  options: UseElapsedTimeOptions = {}
): UseElapsedTimeReturn {
  const { interval = 1000, isActive = true, totalPausedTime = 0, isPaused = false, pausedAt } = options;

  const calculateElapsed = useCallback((): number => {
    if (!startTime) return 0;
    const now = isPaused && pausedAt ? pausedAt : Date.now();
    const gross = now - startTime;
    return Math.max(0, Math.floor((gross - totalPausedTime) / 1000));
  }, [startTime, totalPausedTime, isPaused, pausedAt]);

  const [elapsedSeconds, setElapsedSeconds] = useState(calculateElapsed);

  useEffect(() => {
    if (!startTime || !isActive) {
      setElapsedSeconds(0);
      return;
    }

    // Calculate immediately
    setElapsedSeconds(calculateElapsed());

    // If paused, don't update interval
    if (isPaused) {
      return;
    }

    // Then update at the specified interval
    const timer = setInterval(() => {
      setElapsedSeconds(calculateElapsed());
    }, interval);

    return () => clearInterval(timer);
  }, [startTime, isActive, interval, calculateElapsed, isPaused]);

  // Calculate derived values
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);

  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;

  const formatted =
    hours > 0
      ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      : `${minutes}:${seconds.toString().padStart(2, '0')}`;

  const formattedWithHours = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  return {
    elapsedSeconds,
    elapsedMinutes,
    formatted,
    formattedWithHours,
  };
}

/**
 * Format a duration in minutes to a human-readable string
 *
 * @param minutes - Duration in minutes
 * @returns Human-readable string like "1h 30m" or "45m"
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${mins}m`;
}

/**
 * Format a duration in seconds to a human-readable string
 *
 * @param seconds - Duration in seconds
 * @returns Human-readable string like "1h 30m 15s" or "5m 23s"
 */
export function formatDurationSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }

  return `${secs}s`;
}
